import { Injectable } from '@angular/core';
import { ConsultationStatus } from '@enums/consultation-status';
import { ConsultationTreatmentTypes } from '@enums/consultation-treatment-types';
import { environment } from '@environments/environment';
import { ConsultationRequestFormAlreadySubmittedError } from '@errors/consultation-request-form-already-submitted-error';
import { ConsultationRequestOrderDetail } from '@models/consultation-request/consultation-request-order-detail';
import { ConsultationRequestService } from '@services/consultation-request.service';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class TreatmentConsultationRequestService extends ConsultationRequestService {
  /**
   * Retrieves the consultation order details, fetches additional consultations based on specific filters,
   * and updates the order details with the first consultation from the response.
   *
   * @param {string}                     orderId       the ID of the order to retrieve details for
   * @param {string}                     hash          the hash value associated with the order
   * @param {ConsultationTreatmentTypes} treatmentType the treatment type
   * @param {Record<string,string>} customHeaders  Custom headers to be added to the request (key-value pairs)
   *
   * @returns {Observable<ConsultationRequestOrderDetail>} an Observable that emits the updated order details
   *
   * @throws ConsultationRequestFormAlreadySubmittedError if no consultations are found in the response
   */
  public override getConsultationOrderDetails(
    orderId: string,
    hash: string,
    treatmentType: ConsultationTreatmentTypes,
    customHeaders: Record<string, string> = {},
  ): Observable<ConsultationRequestOrderDetail> {
    this.subscriptionDetails = null;

    return super.getConsultationOrderDetails(orderId, hash, treatmentType, customHeaders).pipe(
      switchMap((orderDetails) => {
        const filters = {
          consultations_status: ConsultationStatus.Incomplete,
          treatment_type: treatmentType,
        };

        return this.getConsultations(orderId, hash, JSON.stringify(filters)).pipe(
          map((response) => {
            if (!response.data?.length && !orderDetails.subscription_current_intake_url) {
              throw new ConsultationRequestFormAlreadySubmittedError(environment.email);
            }

            orderDetails.consultationRequest = response.data[0];
            orderDetails.last_prescribed_at = response['meta']?.last_prescribed_at;
            this.consultationRequestOrderDetail = new ConsultationRequestOrderDetail(orderDetails);

            return orderDetails;
          }),
        );
      }),
      switchMap((orderDetails) => {
        if (!orderDetails.subscription?.external_subscription_id) {
          return of(orderDetails);
        }

        return this.getSubscriptionDetails(orderId, hash).pipe(
          map((subscriptionDetails) => {
            this.subscriptionDetails = subscriptionDetails;

            return orderDetails;
          }),
          catchError((error) => {
            this.errorLoggingService.logHttpError(error, 'Failed to fetch subscription details');

            return of(orderDetails);
          }),
        );
      }),
    );
  }
}
